import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from '../../api/core'
import type { PartyPreset } from '../../components/PartyPresetsPage'
import type { FormState } from '../../types/testdata.types'
import type { LogoState } from './useLogoState'

type PartyRole = 'supplier' | 'buyer'

function storageKey(userId: number, role: PartyRole): string {
  return `testdata-party-preset-${role}-user-${userId}`
}

function readRememberedPresetId(userId: number | null | undefined, role: PartyRole): string {
  if (!userId) return ''
  try {
    return localStorage.getItem(storageKey(userId, role)) ?? ''
  } catch {
    return ''
  }
}

function rememberPresetId(userId: number | null | undefined, role: PartyRole, id: string): void {
  if (!userId) return
  try {
    const key = storageKey(userId, role)
    if (id) localStorage.setItem(key, id)
    else localStorage.removeItem(key)
  } catch { /* localStorage unavailable: selection remains session-only */ }
}

/** Opgeslagen relaties (leverancier/koper-presets) — vult bij keuze de
 * bijbehorende form-velden in, en (alleen voor leverancier) een eventueel
 * eigen logo op de relatie via `logo.selectParty` (zie `logoSelection.ts`).
 *
 * REQ-TESTDATA-009: alleen de geselecteerde preset-id's worden per
 * ingelogde gebruiker in localStorage onthouden. Relatiegegevens zelf blijven
 * authoritative vanuit /api/party-presets en worden nooit gedupliceerd in
 * browseropslag. */
export function usePartyPresets(userId: number | null | undefined) {
  const [partyPresets, setPartyPresets] = useState<PartyPreset[]>([])
  const [presetsLoaded, setPresetsLoaded] = useState(false)
  const [rememberedSelectionUserId, setRememberedSelectionUserId] = useState<number | null>(null)
  const [selectedSupplierPresetId, setSelectedSupplierPresetId] = useState('')
  const [selectedBuyerPresetId, setSelectedBuyerPresetId] = useState('')

  useEffect(() => {
    if (!userId) {
      setSelectedSupplierPresetId('')
      setSelectedBuyerPresetId('')
      setRememberedSelectionUserId(null)
      return
    }
    setSelectedSupplierPresetId(readRememberedPresetId(userId, 'supplier'))
    setSelectedBuyerPresetId(readRememberedPresetId(userId, 'buyer'))
    setRememberedSelectionUserId(userId)
  }, [userId])

  const loadPresets = useCallback(async () => {
    try {
      const data = await apiFetch<PartyPreset[]>('/api/party-presets')
      setPartyPresets(data)
      setPresetsLoaded(true)
    } catch {
      // A transient API failure must not erase a valid remembered choice.
      // Keep restoration pending; the generator remains usable without presets.
      setPartyPresets([])
      setPresetsLoaded(false)
    }
  }, [])

  // A deleted/unavailable remembered relation must never block the generator
  // or leave a stale dropdown value behind. Once the authoritative list is
  // loaded, discard remembered ids that no longer resolve.
  useEffect(() => {
    if (!presetsLoaded || !userId) return
    if (selectedSupplierPresetId && !partyPresets.some(p => String(p.id) === selectedSupplierPresetId)) {
      setSelectedSupplierPresetId('')
      rememberPresetId(userId, 'supplier', '')
    }
    if (selectedBuyerPresetId && !partyPresets.some(p => String(p.id) === selectedBuyerPresetId)) {
      setSelectedBuyerPresetId('')
      rememberPresetId(userId, 'buyer', '')
    }
  }, [partyPresets, presetsLoaded, selectedBuyerPresetId, selectedSupplierPresetId, userId])

  const applySupplierPreset = (
    id: string,
    setForm: React.Dispatch<React.SetStateAction<FormState>>,
    logo: Pick<LogoState, 'selectParty'>,
  ) => {
    const p = partyPresets.find(x => String(x.id) === id)
    if (!p) return
    setSelectedSupplierPresetId(id)
    rememberPresetId(userId, 'supplier', id)
    setForm(f => ({
      ...f,
      supplier_name: p.name, supplier_gln: p.gln,
      supplier_attn: p.attn ?? '', supplier_address: p.address ?? '',
      supplier_postal: p.postal ?? '', supplier_city: p.city ?? '',
      supplier_iban: p.iban ?? '', supplier_vat: p.vat ?? '',
      supplier_kvk: p.kvk ?? '', supplier_phone: p.phone ?? '',
      supplier_email: p.email ?? '', supplier_website: p.website ?? '',
    }))
    // Vorig logo vervalt direct; een relatie zonder (ophaalbaar) logo geeft
    // geen logo — nooit dat van een vorige relatie of het standaardlogo.
    logo.selectParty(p.id, Boolean(p.has_logo))
  }

  const applyBuyerPreset = (id: string, setForm: React.Dispatch<React.SetStateAction<FormState>>) => {
    const p = partyPresets.find(x => String(x.id) === id)
    if (!p) return
    setSelectedBuyerPresetId(id)
    rememberPresetId(userId, 'buyer', id)
    setForm(f => ({
      ...f,
      buyer_name: p.name, buyer_gln: p.gln,
      buyer_attn: p.attn ?? '', buyer_vat: p.vat ?? '',
      buyer_address: p.address ?? '', buyer_postal: p.postal ?? '',
      buyer_city: p.city ?? '',
    }))
  }

  /** Een geladen sjabloon/history/config-preset overschrijft partijvelden
   * buiten de relatiekeuze om. Wis dan ook de remembered provenance, zodat de
   * dropdown nooit een relatie claimt die niet meer bij de zichtbare velden hoort. */
  const resetSelection = () => {
    setSelectedSupplierPresetId('')
    setSelectedBuyerPresetId('')
    rememberPresetId(userId, 'supplier', '')
    rememberPresetId(userId, 'buyer', '')
  }

  /** Naam/GLN van de leverancier handmatig gewijzigd: de zichtbare
   * leverancier is niet langer de gekozen relatie. */
  const resetSupplierSelection = () => {
    setSelectedSupplierPresetId('')
    rememberPresetId(userId, 'supplier', '')
  }

  return {
    partyPresets, presetsLoaded, rememberedSelectionUserId,
    selectedSupplierPresetId, selectedBuyerPresetId,
    loadPresets, applySupplierPreset, applyBuyerPreset, resetSelection, resetSupplierSelection,
  }
}

export type PartyPresetsState = ReturnType<typeof usePartyPresets>
